import * as React from "react";
import { useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import path = require("path");
import DocumentTitle from "../misc/components/DocumentTitle";
import { Link, useParams } from "react-router-dom";
import { Form, useFormApi, useFormState } from "informed";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { ImportSettingModal } from "./import-setting-modal";
import { RenderSettings } from "./setting-renderer";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { AuditModal } from "../auditmodal/components/AuditModal";


interface SettingEditorProps {
    settingPath: string;
}


const SettingEditorForm = ({ settingPath, settingsDefinition }) => {
    const { id } = useParams<{ id: string }>();
    const client = useWebPortalLegacyClient();
    const formApi = useFormApi();
    const [error, setError] = React.useState<String>();
    const [importModalVisible, setImportModalVisible] = React.useState<boolean>(false);
    const [successAlert, setsuccessAlert] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const menuItems = () => {

        return (
            <div className="ibox-tools">
                <button className="btn btn-primary" onClick={exportSettings} disabled={submitting}>
                    Export
                </button>
                &nbsp;
                <button className="btn btn-primary" onClick={() => setImportModalVisible(true)} disabled={submitting}>
                    Import
                </button>
                &nbsp;
                <button className="btn btn-primary" onClick={() => onPreAuditSubmit()} data-automationid="save-button" disabled={submitting}>
                    Save
                </button>
            </div>
        );
    }

    const exportSettings = () => {
        client.getSection(settingPath, id).then(result => {
            const jsonString = JSON.stringify(result.value, null, 2); // pretty-print with 2 spaces

            const blob = new Blob([jsonString], { type: "application/json" });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "study-settings.json";

            link.click();

            URL.revokeObjectURL(link.href);
        }).catch(error => {
            setError(error);
        });

    }

    const onPreAuditSubmit = () => {
        setsuccessAlert(false);
        setError(null);
        setSubmitting(true);

        formApi.validate();
        var formState = formApi.getState();
        if (formState.invalid) {
            setSubmitting(false);

            const getDetailedErrorMessages = (errors, settingsDefinition) => {
                const messages = [];

                const findDetailedErrors = (errorObj, defObj, sectionName, currentPath = []) => {
                    for (const [key, value] of Object.entries(errorObj)) {
                        const newPath = [...currentPath, key];

                        if (typeof value === 'string') {
                            const fullPath = [sectionName, ...newPath];
                            const label = getNestedLabel(settingsDefinition, fullPath);
                            const sectionUpper = sectionName.toUpperCase();//To be improved to show the section label

                            messages.push(`${sectionUpper}: ${label}: ${value}`);

                        } else if (value && typeof value === 'object') {
                            findDetailedErrors(value, defObj?.[key], sectionName, newPath);
                        }
                    }
                };

                // Helper to get nested label from settings definition
                const getNestedLabel = (defObj, path) => {
                    let current = defObj;
                    for (const pathKey of path) {
                        current = current?.[pathKey];
                        if (!current) {
                            return null;
                        }
                    }
                    return current?.label || null;
                };

                for (const [sectionKey, sectionErrors] of Object.entries(errors)) {
                    if (typeof sectionErrors === 'object') {
                        findDetailedErrors(sectionErrors, settingsDefinition.errorSections, sectionKey);
                    }
                }
                return messages;
            };

            const errorMessages = getDetailedErrorMessages(formState.errors, settingsDefinition);
            const errorText = errorMessages.join("; ");

            return setError(`Errors exist in section: ${errorText}`);
        }
        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSaveSettings = async (settings, userAnnotation) => {
        setsuccessAlert(false);
        setSubmitting(true);
        try {
            await client.patchSettingSection(settingPath, settings, id, userAnnotation);
            setsuccessAlert(true);
        } catch (error) {
            setError("Invalid Import.");
        }
        setSubmitting(false);
        setAuditModalVisible(false);
        window.scrollTo(0, 0)
    }

    return (
        <DocumentTitle title={settingsDefinition?.label + " | Vitalograph Web Portal"}>
            {error ? <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert> : null}
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="ibox">
                        <div className="ibox-title">
                            <h2 className="text-primary">{settingsDefinition?.label}</h2>
                            {menuItems()}
                        </div>
                        <div className="ibox-content">
                            <div className="row">
                                <RenderSettings setting={settingsDefinition} />
                            </div>
                        </div>
                        <div className="ibox-footer">
                            {menuItems()}
                        </div>
                    </div>
                </div>
            </div>
            {
                !importModalVisible ? null :
                    <ImportSettingModal onClose={() => setImportModalVisible(false)} onSave={(settings) => onSaveSettings(settings, "")} />
            }
            {
                auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={(audit) => onSaveSettings(formApi.getState().values, audit.Annotation)}
                    />
                ) : null}
        </DocumentTitle>
    );
}



export const SettingEditor = ({ settingPath }: SettingEditorProps) => {
    const { id } = useParams<{ id: string }>();
    const client = useWebPortalLegacyClient();
    const [settingsDefinition, setSettingsDefinition] = React.useState<any>();
    const [initialValues, setInitialValues] = React.useState<any>();

    React.useEffect(() => {
        loadValues();
        loadSettingsDefinition();
    }, [settingPath])



    const loadValues = async () => {
        var settingsDefinition = (await client.getSectionDefinition(settingPath, id)).value
        setSettingsDefinition(settingsDefinition);
    }

    const loadSettingsDefinition = async () => {
        var initialValues = (await client.getSection(settingPath, id)).value
        setInitialValues(initialValues);
    }

    if (!settingsDefinition || !initialValues)
        return <LoadingWithMessage message="Loading Setting..." />

    return (
        <Form initialValues={initialValues} autoComplete="off">
            <SettingEditorForm settingsDefinition={settingsDefinition} settingPath={settingPath} />
        </Form>
    )
}








